import { useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { useExport } from '@/hooks/useExport';

interface ExportMenuProps {
  /** Data rows to export */
  data: Record<string, unknown>[];
  /** Base filename without extension or timestamp */
  filenameBase: string;
  /** Disable the export button */
  disabled?: boolean;
}

/**
 * ExportMenu — dropdown button offering CSV and JSON download options.
 * Calls useExport hook for client-side blob download.
 */
export default function ExportMenu({ data, filenameBase, disabled = false }: ExportMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { downloadCsv, downloadJson } = useExport();

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportCsv = () => {
    downloadCsv(data, filenameBase);
    handleClose();
  };

  const handleExportJson = () => {
    downloadJson(data, filenameBase);
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={handleOpen}
        disabled={disabled}
        aria-controls={open ? 'export-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        Export
      </Button>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'export-button' }}
      >
        <MenuItem onClick={handleExportCsv}>Export CSV</MenuItem>
        <MenuItem onClick={handleExportJson}>Export JSON</MenuItem>
      </Menu>
    </>
  );
}
